import { useState } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import Grid from '@mui/material/Grid2';
import { Box } from '@mui/material';

import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import logger from 'utils/logger';

import { useMaterialUIController } from 'context';
import { ThemeProvider } from '@mui/material/styles';
import { getTablesTheme } from 'components/CustomThemes';
import Privilege from './PriviledegeModal';
import { userManagementColumns } from './userManagementColumns';
import BasicTable from 'components/BasicTable';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { useSnackbar } from 'notistack';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';

const propTypes = {
  organizationInfo: PropTypes.object,
  fetchOrganizationInfo: PropTypes.func,
};

// Create a logger instance for this component
const log = logger.createLogger('UserManagementTab');

const UserManagementTab = ({ organizationInfo, fetchOrganizationInfo }) => {
  const snackbar = useSnackbar();

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [privilegeDetailsModalOpen, setPrivilegeDetailsModalOpen] =
    useState(false);

  const [privilegeDetails, setPrivilegeDetails] = useState({});
  const [currentPrivilege, setCurrentPrivilege] = useState(null);

  const handleOpenPrivilegeModal = (details) => {
    setPrivilegeDetails(details);
    setCurrentPrivilege(JSON.parse(details.employment.privilege));
    setPrivilegeDetailsModalOpen(true);
  };

  const handleUpdatePrivilege = async () => {
    if (!currentPrivilege || !privilegeDetails.employment) return;

    api
      .put(DOLPHIN_API.EMPLOYMENT_PRIVILEGE, {
        params: {
          organizationId: organizationInfo.id,
        },
        data: {
          employmentId: privilegeDetails.employment.id,
          newPrivilege: currentPrivilege,
        },
      })
      .then(
        () => {
          setPrivilegeDetailsModalOpen(false);
          setPrivilegeDetails({});
          setCurrentPrivilege(null);
          fetchOrganizationInfo();
          // Reload the page to update the privilege only if the user is the same
          if (
            privilegeDetails.employment.userId ===
            parseInt(localData.get(USER_DATA.USER_ID))
          ) {
            snackbar.enqueueSnackbar(
              intl.get('shared_data_was_updated_with_reload'),
              {
                variant: 'success',
              }
            );
            setTimeout(() => {
              window.location.reload();
            }, 1000); // 2 seconds delay
          } else {
            snackbar.enqueueSnackbar(intl.get('shared_data_was_updated'), {
              variant: 'success',
            });
          }
        },
        (error) => {
          log.error('Failed in updating privilege', { error });
          snackbar.enqueueSnackbar(intl.get('shared_data_was_updated_error'), {
            variant: 'error',
          });
        }
      );
  };

  const users = organizationInfo.users.sort(
    (user1, user2) => user1.id - user2.id
  );

  const employments = organizationInfo.employments.sort(
    (employment1, employment2) => employment1.userId - employment2.userId
  );

  if (users.length !== employments.length) {
    log.warn('alert users.length !== employments.length');
  }

  let tableData = [];
  for (let i = 0; i < users.length; i++) {
    const user = users[i];
    const employment = employments[i];
    const rowData = {
      username: user.username,
      userFullName: user.firstName + ' ' + user.lastName,
      email: user.email,
      privilege: { employment, user },
    };
    tableData.push(rowData);
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Box mt={1} mb={2}>
            <ThemeProvider theme={getTablesTheme(darkMode)}>
              <BasicTable
                columns={userManagementColumns(
                  handleOpenPrivilegeModal,
                  setPrivilegeDetailsModalOpen
                )}
                data={tableData}
                tableTitle={intl.get(
                  'organization_settings_user_management_text_staffs'
                )}
                darkMode={darkMode}
                handleRefreshClick={fetchOrganizationInfo}
                enableRowSelection={false}
              />
            </ThemeProvider>
          </Box>
        </Grid>
      </Grid>

      <ConfirmModal
        isOpened={privilegeDetailsModalOpen}
        onCancel={() => {
          setPrivilegeDetailsModalOpen(false);
          setPrivilegeDetails({});
          setCurrentPrivilege(null);
        }}
        title={intl.get(
          'organization_settings_user_management_modal_text_privilege_details'
        )}
        confirmButtonText={intl.get('personal_settings_button_update')}
        cancelButtonText={intl.get(
          'personal_settings_preference_modal_button_cancel'
        )}
        onConfirm={handleUpdatePrivilege}>
        <div style={{ width: '500px' }}>
          <Privilege
            privilegeDetails={privilegeDetails}
            privilege={currentPrivilege}
            onPrivilegeChange={setCurrentPrivilege}
          />
        </div>
      </ConfirmModal>
    </>
  );
};

UserManagementTab.propTypes = propTypes;

export default UserManagementTab;
